import {
	autoinject,
	bindable,
	bindingMode,
	customElement,
	inlineView,
} from 'aurelia-framework';
import React from 'react';

import { Autocomplete } from '~/components/field/common/autocomplete/autocomplete';
import { ReactComponent } from '~/components/react-component/react-component';
import { FieldNameParamType } from '~/utils/acurity-field-utils';

export interface AutocompleteColumnHeader {
	columnName: string;
	display: string;
	hidden?: boolean;
	formatter?: (displayValue: any) => any;
	tokenizer?: (str: string) => string[];
	render?: (suggestion: any) => string;
}

export interface AutocompleteOptions {
	limit: number;
	allowEmptyQuery?: boolean;
	remoteOptions?: any;
	fieldTypes?: readonly FieldNameParamType[];
}

export interface AutocompleteRemoteLoader {
	getResults(
		query: string,
		options: AutocompleteOptions,
	): Promise<any> | undefined;
}

// NOTE: Jake: 2018-10-26
// Not extending "Field" by design.
// All fields are also private by design. This class should not be extended, but merely used
// or composed with other components. (ie. See suburb.ts)
//
// This is low-level library meant to be used by classes that extend "Field" like Suburb.
// I want to keep it lightweight so that it's easier to unit test in the future.
//
// New South Wales: 1000-2599, 2620-2899, 2921-2999
//
@autoinject
@customElement('autocomplete-react')
@inlineView('<template ref="refContainer"></template>')
export class AutocompleteReact extends ReactComponent {
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private id: string = '';

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private items?: any[];

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private isLoading?: boolean;

	@bindable({
		defaultBindingMode: bindingMode.twoWay,
	})
	private value: string = '';

	/**
	 * Readonly description displayed next to value only when input element is not focused.
	 * @see this.hasFocus
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private description: string;

	/**
	 * Utilized by End-To-End tests to get <input>/<select>/<textarea> fields.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private dataFieldId?: string;

	/**
	 * Set the tooltip
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private title?: string;

	/**
	 * A class or object that implements the "AutocompleteRemoteLoader" interface.
	 * ie. "api/myendpoint?search=%QUERY"
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private remote?: AutocompleteRemoteLoader;

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private remoteOptions?: { [option: string]: any | undefined };

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private columnName: string;

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private columnHeaders?: AutocompleteColumnHeader[];

	/**
	 * The width of the autocomplete list.
	 * This can be overriden so that it can be longer than the length
	 * of the <input> element.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private listWidth?: number;

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private disabled?: boolean;

	/**
	 * Automatically display results on focus if there is no value provided. If field has prefetched
	 * search items, and you want results to automatically display, remember to bind this to true.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private displayResultsOnFocus: boolean = false;

	/**
	 * refInputElem bindable allows us to retrieve the <input> field from
	 * this autocomplete component.
	 */
	@bindable({
		defaultBindingMode: bindingMode.fromView,
	})
	private refInputElem: HTMLInputElement;

	/**
	 * onValidationError will fire if mustRequireSelectFromList() is true
	 * and the value is reset to blank.
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private onValidationError?: (hasError: boolean) => void;

	/**
	 * This callback is triggered when the user selects an autocomplete item
	 * and provides the full autocomplete record so that other data on the record
	 * can be used to populate other input fields.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private onSelect?: (record?: { [columnName: string]: any }) => void;

	/**
	 * This callback is triggered when the user component requests a search.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private onSearchStarted?: () => void;

	/**
	 * This callback is triggered when the a search is canceled by the user.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private onSearchCanceled?: () => void;

	/**
	 * This callback is triggered when the an ongoing search request completes without being canceled by the user.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private onSearchCompleted?: () => void;

	/**
	 * This holds the value of autocomplete attribute of field's input tag. Since this field itself is responsible for
	 * its autocomplete behavior so the browser's autocomplete has to be disabled through this.
	 * It must be 'off' for search fields and some invalid value for autofill fields like address, cc details etc to prevent
	 * chrome from displaying suggestions.
	 * Refer WEBUI-1678 for details
	 */
	@bindable
	private autocomplete: string = 'off';

	/**
	 * True if this field should allow user to type in any value in addition to selecting one from search results
	 */
	@bindable
	private allowUserInput: boolean = false;

	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	private fieldTypes?: readonly FieldNameParamType[];

	constructor(protected element: Element) {
		super('autocomplete-react', element);
		// These two bindings need to be lifted up the chain. See: https://reactjs.org/docs/lifting-state-up.html
		this.handleValueChange = this.handleValueChange.bind(this);
		this.handleRefInputElemChange =
			this.handleRefInputElemChange.bind(this);
	}

	private handleValueChange(value: string): void {
		this.value = value;
	}

	private handleRefInputElemChange(refInputElem: HTMLInputElement): void {
		this.refInputElem = refInputElem;
	}

	/**
	 * @override
	 */
	protected render() {
		this.renderReactDOM(
			<Autocomplete
				id={this.id}
				columnHeaders={this.columnHeaders}
				columnName={this.columnName}
				items={this.items}
				value={this.value}
				listWidth={this.listWidth}
				disabled={this.disabled}
				remote={this.remote}
				remoteOptions={this.remoteOptions}
				dataFieldId={this.dataFieldId}
				title={this.title}
				onSelect={this.onSelect}
				onValidationError={this.onValidationError}
				onSearchStarted={this.onSearchStarted}
				onSearchCanceled={this.onSearchCanceled}
				onSearchCompleted={this.onSearchCompleted}
				displayResultsOnFocus={this.displayResultsOnFocus}
				autocomplete={this.autocomplete}
				allowUserInput={this.allowUserInput}
				description={this.description}
				handleValueChange={this.handleValueChange}
				handleRefInputElemChange={this.handleRefInputElemChange}
				fieldTypes={this.fieldTypes}
			/>,
		);
	}
}
